import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../../../api/apiClient';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorBanner from '../../../components/ui/ErrorBanner';
import { st, colors } from '../shared/meetingStyles';
import { FileTextIcon, PlayIcon, PauseIcon, ZapIcon, XIcon } from '../shared/Icons';

const SpeechRecognitionCtor = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

function matchOwnerId(guess, team) {
  if (!guess) return '';
  const g = guess.trim().toLowerCase();
  if (!g) return '';
  const match = team.find(u => {
    const full = (u.name || '').toLowerCase();
    const first = full.split(' ')[0];
    return full.includes(g) || g.includes(first);
  });
  return match ? match.id : '';
}

function ReviewModal({ summary, team, meetingId, sessionId, onClose, onDone }) {
  const [todos, setTodos] = useState((summary.todos || []).map(t => ({ ...t, include: true, ownerUserId: matchOwnerId(t.ownerNameGuess, team) })));
  const [issues, setIssues] = useState((summary.issues || []).map(i => ({ ...i, include: true })));
  const [headlines, setHeadlines] = useState((summary.headlines || []).map(h => ({ ...h, include: true, ownerUserId: matchOwnerId(h.ownerNameGuess, team) })));
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    try {
      for (const t of todos.filter(t => t.include && t.title.trim())) {
        await apiClient.post('/meetings/todos', { title: t.title.trim(), owner_user_id: t.ownerUserId || null, meeting_id: meetingId, session_id: sessionId });
      }
      for (const i of issues.filter(i => i.include && i.title.trim())) {
        await apiClient.post('/meetings/issues', { title: i.title.trim(), meeting_id: meetingId });
      }
      for (const h of headlines.filter(h => h.include && h.text.trim())) {
        await apiClient.post('/meetings/headlines', { text: h.text.trim(), owner_user_id: h.ownerUserId || null, meeting_id: meetingId, session_id: sessionId });
      }
      onDone();
    } finally {
      setSaving(false);
    }
  };

  const totalIncluded = todos.filter(t => t.include).length + issues.filter(i => i.include).length + headlines.filter(h => h.include).length;

  return (
    <div style={modalSt.overlay}>
      <div style={modalSt.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ ...st.h3, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><ZapIcon size={15} style={{ color: colors.amber }} /> AI Meeting Summary</h3>
          <button onClick={onClose} style={st.ghostBtn}><XIcon size={13} /></button>
        </div>

        {summary.notes && (
          <div style={{ background: colors.bgSubtle, borderRadius: '0.6rem', padding: '0.7rem 0.9rem', marginBottom: '1rem', fontSize: '0.78rem', color: colors.text }}>
            {summary.notes}
          </div>
        )}

        <div style={modalSt.scrollArea}>
          {todos.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={modalSt.groupLabel}>To-dos</div>
              {todos.map((t, idx) => (
                <div key={idx} style={modalSt.row}>
                  <input type="checkbox" checked={t.include} onChange={e => setTodos(ts => ts.map((x, i) => i === idx ? { ...x, include: e.target.checked } : x))} />
                  <input value={t.title} onChange={e => setTodos(ts => ts.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))} style={{ ...st.input, flex: 1 }} />
                  <select value={t.ownerUserId} onChange={e => setTodos(ts => ts.map((x, i) => i === idx ? { ...x, ownerUserId: e.target.value } : x))} style={{ ...st.input, width: '130px' }}>
                    <option value="">No owner</option>
                    {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
          {issues.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={modalSt.groupLabel}>Issues</div>
              {issues.map((i, idx) => (
                <div key={idx} style={modalSt.row}>
                  <input type="checkbox" checked={i.include} onChange={e => setIssues(is => is.map((x, ix) => ix === idx ? { ...x, include: e.target.checked } : x))} />
                  <input value={i.title} onChange={e => setIssues(is => is.map((x, ix) => ix === idx ? { ...x, title: e.target.value } : x))} style={{ ...st.input, flex: 1 }} />
                </div>
              ))}
            </div>
          )}
          {headlines.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={modalSt.groupLabel}>Headlines</div>
              {headlines.map((h, idx) => (
                <div key={idx} style={modalSt.row}>
                  <input type="checkbox" checked={h.include} onChange={e => setHeadlines(hs => hs.map((x, ix) => ix === idx ? { ...x, include: e.target.checked } : x))} />
                  <input value={h.text} onChange={e => setHeadlines(hs => hs.map((x, ix) => ix === idx ? { ...x, text: e.target.value } : x))} style={{ ...st.input, flex: 1 }} />
                  <select value={h.ownerUserId} onChange={e => setHeadlines(hs => hs.map((x, ix) => ix === idx ? { ...x, ownerUserId: e.target.value } : x))} style={{ ...st.input, width: '130px' }}>
                    <option value="">No owner</option>
                    {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
          {todos.length === 0 && issues.length === 0 && headlines.length === 0 && (
            <div style={{ ...st.subtext, padding: '1rem 0' }}>Nothing actionable found in the transcript.</div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button onClick={onClose} style={st.secondaryBtn}>Cancel</button>
          <button onClick={confirm} disabled={saving || totalIncluded === 0} style={st.primaryBtn}>
            {saving ? 'Creating...' : `Create ${totalIncluded} item${totalIncluded !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TranscriptSection({ session, meetingId }) {
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [recording, setRecording] = useState(false);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');
  const saveTimerRef = useRef(null);

  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);

  const load = useCallback(async () => {
    if (!session) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [tRes, teamRes] = await Promise.all([
        apiClient.get(`/meetings/sessions/${session.id}/transcript`),
        apiClient.get('/meetings/team'),
      ]);
      setTranscript(tRes.data.transcript || '');
      setTeam(teamRes.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { load(); }, [load]);

  const saveTranscript = useCallback(async () => {
    if (!session) return;
    try { await apiClient.put(`/meetings/sessions/${session.id}/transcript`, { transcript: transcriptRef.current }); } catch {}
  }, [session]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
  }, []);

  const startRecording = () => {
    if (!SpeechRecognitionCtor) {
      setError('Live transcription needs Chrome or Edge - this browser doesn\'t support it.');
      return;
    }
    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      let interimText = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += t + ' ';
        else interimText += t;
      }
      if (finalText) setTranscript(prev => (prev ? prev + ' ' : '') + finalText.trim());
      setInterim(interimText);
    };
    recognition.onerror = (event) => {
      if (event.error !== 'no-speech') setError(`Mic error: ${event.error}`);
    };
    recognition.onend = () => {
      if (recognitionRef.current === recognition) recognition.start(); // auto-restart, browsers stop after ~60s of silence
    };
    recognition.start();
    recognitionRef.current = recognition;
    setRecording(true);
    saveTimerRef.current = setInterval(saveTranscript, 15000);
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      const r = recognitionRef.current;
      recognitionRef.current = null;
      r.stop();
    }
    if (saveTimerRef.current) { clearInterval(saveTimerRef.current); saveTimerRef.current = null; }
    setInterim('');
    setRecording(false);
    saveTranscript();
  };

  const summarize = async () => {
    setSummarizing(true);
    setError(null);
    try {
      await saveTranscript();
      const res = await apiClient.post(`/meetings/sessions/${session.id}/summarize`);
      setSummary(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSummarizing(false);
    }
  };

  if (!session) return <div style={st.card}><div style={st.emptyState}><FileTextIcon size={28} style={{ color: colors.faint }} />Start the meeting to capture a transcript.</div></div>;
  if (loading) return <LoadingSpinner />;

  return (
    <div style={st.card}>
      {error && <ErrorBanner message={error} />}
      <div style={st.cardHeader}>
        <h2 style={{ ...st.h2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FileTextIcon size={18} /> Transcript</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {!recording ? (
            <button onClick={startRecording} style={{ ...st.primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><PlayIcon size={13} /> Start Recording</button>
          ) : (
            <button onClick={stopRecording} style={{ ...st.secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: colors.red, borderColor: colors.red }}><PauseIcon size={13} /> Stop</button>
          )}
          <button onClick={summarize} disabled={summarizing || !transcript.trim()} style={{ ...st.secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <ZapIcon size={13} style={{ color: colors.amber }} /> {summarizing ? 'Summarizing...' : 'Summarize with AI'}
          </button>
        </div>
      </div>

      {!SpeechRecognitionCtor && (
        <div style={{ fontSize: '0.72rem', color: colors.amber, marginBottom: '0.75rem' }}>
          Live transcription requires Chrome or Edge. You can still paste a transcript below manually.
        </div>
      )}

      {recording && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem', fontSize: '0.72rem', color: colors.red, fontWeight: '700' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors.red, boxShadow: `0 0 0 3px ${colors.redSoft}` }} />
          Recording...
        </div>
      )}

      <textarea
        value={transcript + (interim ? (transcript ? ' ' : '') + interim : '')}
        onChange={e => setTranscript(e.target.value)}
        onBlur={saveTranscript}
        placeholder="Transcript will appear here as the meeting is recorded, or paste one in manually..."
        style={{ ...st.textarea, width: '100%', minHeight: '260px' }}
      />

      {summary && (
        <ReviewModal summary={summary} team={team} meetingId={meetingId} sessionId={session.id}
          onClose={() => setSummary(null)}
          onDone={() => setSummary(null)} />
      )}
    </div>
  );
}

const modalSt = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem',
  },
  modal: {
    background: '#fff', borderRadius: '1rem', padding: '1.25rem 1.4rem', width: '560px',
    maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  scrollArea: { overflowY: 'auto', flex: 1 },
  groupLabel: { fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.faint, marginBottom: '0.5rem' },
  row: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' },
};
